export interface CustomerPaymentProofUploadCommand {
  readonly file: File;
  readonly businessDate: string;
  readonly uploadedAt: string;
}

export interface CustomerPaymentProofUploadResult {
  readonly storageRef: string;
}

/**
 * Deployment-owned transport capability. Implementations may use R2 or another
 * durable object store, but must return a stable reference that staff devices
 * can resolve later. Browser blob/data URLs are intentionally rejected.
 */
export interface CustomerPaymentProofUploadPort {
  readonly upload: (command: CustomerPaymentProofUploadCommand) => Promise<CustomerPaymentProofUploadResult>;
}

const isEphemeralBrowserRef = (value: string): boolean => /^(blob|data):/i.test(value);

export async function uploadCustomerPaymentProof(
  port: CustomerPaymentProofUploadPort,
  command: CustomerPaymentProofUploadCommand,
): Promise<string> {
  if (!(command.file instanceof File) || command.file.size <= 0) {
    throw new Error('CUSTOMER_PAYMENT_PROOF_FILE_REQUIRED');
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(command.businessDate)) {
    throw new Error('CUSTOMER_PAYMENT_PROOF_BUSINESS_DATE_INVALID');
  }
  if (!Number.isFinite(Date.parse(command.uploadedAt))) {
    throw new Error('CUSTOMER_PAYMENT_PROOF_TIME_INVALID');
  }

  const result = await port.upload(command);
  const storageRef = result.storageRef?.trim() ?? '';
  if (!storageRef || isEphemeralBrowserRef(storageRef)) {
    throw new Error('CUSTOMER_PAYMENT_PROOF_DURABLE_REF_REQUIRED');
  }
  return storageRef;
}
